"use client";

import type { ReactNode } from "react";
import {
  LuChartColumn as LuBarChart2,
  LuGem,
  LuLayers,
  LuQuote,
  LuRepeat2,
  LuShare,
  LuSmile,
} from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { focusIOSKeyboardBridge } from "@/components/app/editor-v3/editor-dom-focus";
import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { usePostContext } from "@/contexts/post-context";
import { useReactionLogic } from "@/hooks/use-reaction-logic";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { useSharePost } from "@/hooks-api/use-share-post";
import { DESO_APP_NAME } from "@/utils/constants";
import { formatUSDCents } from "@/utils/currency";
import { DEFAULT_POST_DATE_FORMAT, FocusDateParser } from "@/utils/date";
import { ActivityModalType } from "@/utils/post-utils";

import { PostReactionSlider } from "../post-reaction-slider";
import { PostDiamonds } from "./post-diamonds";
import { PostEmojiActivity } from "./post-emoji-activity";

// Note: this component is used only in single post view, and post NEED to be wrapped in SinglePostContext
export const PostActivity = () => {
  const { appState } = useFocusMarketData();

  const {
    post,
    repost,
    isPureRepost,
    publicKey: creatorPublicKey,
    timestamp,
    postHash,
  } = usePostContext();
  const activityPostHash = isPureRepost ? repost?.postHash : post?.postHash;

  const appId = post?.extraData.Node;
  const appName = appState?.Nodes[Number(appId)]?.Name || DESO_APP_NAME;

  const stats = useGetCurrentUserPostStats(post);

  const {
    repostCount,
    viewCount,
    loading,
    totalReactionsCount,
    quoteCount,
    diamondsTotalCount,
    diamondsTotalUsdCents,
    hasCurrentPostRepostedByCurrentUser,
    hasCurrentPostQuotedByCurrentUser,
  } = stats;

  const { hasAtLeastOneUserReaction } = useReactionLogic({ postHash });

  const renderNumber = (data: ReactNode) =>
    loading ? <Skeleton className="m-auto h-[20px] w-[50px]" /> : data;

  const renderPostActivityModal = ({
    trigger,
    activity,
  }: {
    trigger: ReactNode;
    activity: ActivityModalType;
  }) => (
    <div
      className="
        cursor-pointer
        hover:text-muted-foreground
      "
      key={activity}
      onClick={() => {
        if (!activityPostHash) {
          return;
        }

        setDialog(DIALOG.PostActivity, {
          open: true,
          params: {
            activity,
            postHash: activityPostHash,
          },
        });
      }}
    >
      {renderNumber(trigger)}
    </div>
  );
  const activities = [
    {
      label: `${totalReactionsCount} Reactions`,
      type: ActivityModalType.reaction,
    },
    { label: `${repostCount} Reposts`, type: ActivityModalType.repost },
    {
      label: `${quoteCount} Quotes`,
      type: ActivityModalType.quotedRepost,
    },
    {
      label: `${diamondsTotalCount} Diamonds (${formatUSDCents(
        diamondsTotalUsdCents!,
      )})`,
      type: ActivityModalType.diamond,
    },
  ];

  const { onShare, loading: shareLoading } = useSharePost(postHash);

  const onShareClick = async () => {
    await onShare();
  };

  return (
    <div className="mb-0">
      <div
        className="
          p-3
          md:px-8
        "
      >
        <PostEmojiActivity />

        <div
          className="
            mt-2 flex w-full items-center justify-between
            sm:mb-4
          "
        >
          <div className="text-xs text-muted">
            Posted on{" "}
            <span className="underline underline-offset-4">{appName}</span>{" "}
            •&nbsp;
            {FocusDateParser.formatUTCDate(
              timestamp,
              DEFAULT_POST_DATE_FORMAT,
              "Pending...",
            )}
          </div>

          {renderPostActivityModal({
            trigger: (
              <div
                className="
                  ml-auto flex h-[20px] cursor-pointer items-center gap-2
                  text-right text-xs text-muted
                  hover:text-muted-foreground
                "
              >
                <LuBarChart2 className="text-lg" />
                {renderNumber(viewCount)}
              </div>
            ),
            activity: ActivityModalType.view,
          })}
        </div>
      </div>
      <div
        className="
          unforce-radius flex items-center border-y border-border-light p-3
          text-sm text-muted
        "
      >
        <div
          className="
            grid w-full grid-cols-2 items-start justify-start gap-2 text-left
            md:flex md:grid-cols-4 md:justify-around
          "
        >
          {activities.map(({ label, type }) =>
            renderPostActivityModal({
              trigger: <div>{label}</div>,
              activity: type,
            }),
          )}
        </div>
      </div>

      <div
        className="
          unforce-radius border-b border-border-light py-4 text-sm text-muted
        "
      >
        <div className="flex items-center justify-around text-center">
          <div className="flex items-center justify-center gap-2">
            <TriggerLoggedInModalWindow>
              <PostReactionSlider
                {...{
                  postHash,
                  customTrigger: (
                    <div
                      className={buttonVariants({
                        variant: hasAtLeastOneUserReaction
                          ? "outlinePrimary"
                          : "outline",
                        size: "icon",
                      })}
                      role="button"
                    >
                      <LuSmile className="text-lg" />
                    </div>
                  ),
                }}
              />
            </TriggerLoggedInModalWindow>
          </div>

          <div className="flex items-center justify-center gap-2">
            <TriggerLoggedInModalWindow>
              <Button
                variant={
                  hasCurrentPostRepostedByCurrentUser
                    ? "outlinePrimary"
                    : "outline"
                }
                size="icon"
                loading={shareLoading}
                onClick={onShareClick}
                className="
                  cursor-pointer
                  disabled:opacity-100
                "
                disabled={hasCurrentPostRepostedByCurrentUser}
              >
                <LuRepeat2 className="text-lg" />
              </Button>
            </TriggerLoggedInModalWindow>
          </div>

          <TriggerLoggedInModalWindow>
            <div className="flex items-center justify-center gap-2">
              <Button
                variant={
                  hasCurrentPostQuotedByCurrentUser
                    ? "outlinePrimary"
                    : "outline"
                }
                size="icon"
                onClick={() => {
                  focusIOSKeyboardBridge();
                  setDialog(DIALOG.QuotePost, {
                    open: true,
                    params: {
                      postHash,
                    },
                    props: {
                      post,
                    },
                  });
                }}
              >
                <LuQuote className="text-lg" />
              </Button>
            </div>
          </TriggerLoggedInModalWindow>

          <TriggerLoggedInModalWindow>
            <PostDiamonds
              {...{
                postHash,
                creatorPublicKey,
                customHoverTrigger: (
                  hasCurrentUserSentDiamondsToPost: boolean,
                ) => (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant={
                        hasCurrentUserSentDiamondsToPost
                          ? "outlinePrimary"
                          : "outline"
                      }
                      size="icon"
                    >
                      <LuGem className="text-lg" />
                    </Button>
                  </div>
                ),
              }}
            />
          </TriggerLoggedInModalWindow>

          <AddToCollectionDropdown
            types={["bookmark"]}
            createText="New Bookmark Collection"
            itemData={{
              type: "post",
              postHash,
            }}
          >
            <Button variant="outline" size="icon">
              <LuLayers className="text-lg" />
            </Button>
          </AddToCollectionDropdown>

          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                setDialog(DIALOG.PageShare, {
                  open: true,
                })
              }
            >
              <LuShare className="text-lg" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
